/**
 * Scoped draft persistence (Wave 3 — R2-BR-D12 family).
 *
 * Root cause of the cross-session contamination: draft mirrors were
 * keyed ONLY by record (`gn:draft:<proposalId>:<sectionId>`), with no
 * user / organization / beneficiary dimension. A MEMBER's note edit
 * was rejected by the server (403), but the rejected text survived in
 * the device mirror; when the OWNER later opened the same workspace
 * in the same browser, the compliance editor's restore effect loaded
 * the mirror and autosaved it — a rejected write became an authorized
 * user's write.
 *
 * Every content-bearing draft now lives under a scope prefix:
 *   gn:draft:<userId>:<orgId>:<beneficiaryId|'-'>:<record parts…>
 * Reads/writes only ever touch the caller's own scope. On logout,
 * session expiry, user switch, or workspace switch, every draft key
 * outside the now-current scope is purged (see purgeForeignDrafts,
 * wired in AuthContext). Legacy Wave-2-format keys
 * (`gn:draft:<proposalId>:…`) match NO scope prefix, so the first
 * sweep under any real scope removes them.
 */

export interface DraftScope {
  userId: string;
  orgId: string;
  /** Individual workspaces scope drafts per beneficiary; org surfaces pass null. */
  beneficiaryId?: string | null;
}

export interface DraftMirrorValue {
  body: string;
  updatedAt: number;
}

export const DRAFT_KEY_PREFIX = 'gn:draft:';

const segment = (value: string): string => encodeURIComponent(value);

export function draftScopePrefix(scope: DraftScope): string {
  return (
    DRAFT_KEY_PREFIX +
    `${segment(scope.userId)}:${segment(scope.orgId)}:${segment(scope.beneficiaryId ?? '-')}:`
  );
}

export function scopedDraftKey(scope: DraftScope, ...parts: string[]): string {
  return draftScopePrefix(scope) + parts.map(segment).join(':');
}

function storages(): Storage[] {
  const found: Storage[] = [];
  try {
    found.push(window.localStorage);
  } catch {
    /* unavailable */
  }
  try {
    found.push(window.sessionStorage);
  } catch {
    /* unavailable */
  }
  return found;
}

/** Drafts persist in localStorage (crash/reload recovery); sessionStorage is swept too for safety. */
function primaryStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readScopedDraft(scope: DraftScope, ...parts: string[]): DraftMirrorValue | null {
  const storage = primaryStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(scopedDraftKey(scope, ...parts));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DraftMirrorValue>;
    if (typeof parsed.body !== 'string' || typeof parsed.updatedAt !== 'number') return null;
    return { body: parsed.body, updatedAt: parsed.updatedAt };
  } catch {
    return null;
  }
}

export function writeScopedDraft(scope: DraftScope, body: string, ...parts: string[]): void {
  const storage = primaryStorage();
  if (!storage) return;
  try {
    const mirror: DraftMirrorValue = { body, updatedAt: Date.now() };
    storage.setItem(scopedDraftKey(scope, ...parts), JSON.stringify(mirror));
  } catch {
    // Storage can be full or unavailable; the server save state remains
    // the primary signal and surfaces any real failure.
  }
}

export function removeScopedDraft(scope: DraftScope, ...parts: string[]): void {
  const storage = primaryStorage();
  if (!storage) return;
  try {
    storage.removeItem(scopedDraftKey(scope, ...parts));
  } catch {
    /* nothing actionable */
  }
}

/**
 * Remove every draft key that does not belong to `scope` — including
 * all legacy unscoped keys. `scope === null` (logout / session expiry)
 * removes ALL draft keys. Returns how many keys were removed.
 */
export function purgeForeignDrafts(scope: DraftScope | null): number {
  const keepPrefix = scope ? draftScopePrefix(scope) : null;
  let removed = 0;
  for (const storage of storages()) {
    try {
      const doomed: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (!key || !key.startsWith(DRAFT_KEY_PREFIX)) continue;
        if (keepPrefix && key.startsWith(keepPrefix)) continue;
        doomed.push(key);
      }
      for (const key of doomed) {
        storage.removeItem(key);
        removed++;
      }
    } catch {
      /* storage unavailable — nothing to purge */
    }
  }
  return removed;
}

/** True when a failed save means THIS session may not write the record
 *  (signed out / session expired, role cannot write, record gone):
 *  the pending write must be dropped and its mirror deleted, never
 *  requeued — a rejected write must not become a later write. */
export function isRejectedWrite(status: number | undefined): boolean {
  return status === 401 || status === 403 || status === 404;
}
